import {invalidateOnSuccess, omneticApi} from '@dms/api/base';

import {
  AddTemplateV3ApiArg,
  AddTemplateV3ApiResponse,
  CreateCustomFieldPropertyApiArg,
  CreateCustomFieldPropertyApiResponse,
  DownloadTemplateApiArg,
  DownloadTemplateApiArgV3,
  DownloadTemplateApiResponse,
  DownloadTemplateApiResponseV3,
  DownloadTemplateSourceApiArgV3,
  DownloadTemplateSourceApiResponseV3,
  GetDefinitionTemplateListApiArg,
  GetDefinitionTemplateListApiResponse,
  GetDocumentDefinitionListApiArg,
  GetDocumentDefinitionListApiResponse,
  GetDocumentGroupListApiArg,
  GetDocumentGroupListApiResponse,
  HideTemplateApiArg,
  HideTemplateApiResponse,
  ListCustomFieldPropertyApiArg,
  ListCustomFieldPropertyApiResponse,
  PatchTemplateApiArg,
  PatchTemplateApiResponse,
  RemoveCustomFieldPropertyApiArg,
  RemoveCustomFieldPropertyApiResponse,
  RemoveTemplateApiArg,
  RemoveTemplateApiResponse,
  SetPrimaryAuthProfileApiArg,
  SetPrimaryAuthProfileApiResponse,
  SetPrimaryTemplateApiArgV3,
  SetPrimaryTemplateApiResponseV3,
  ShowTemplateApiArg,
  ShowTemplateApiResponse,
} from './types';

export const documentApi = omneticApi.injectEndpoints({
  endpoints: (build) => ({
    downloadTemplate: build.query<DownloadTemplateApiResponse, DownloadTemplateApiArg>({
      query: (queryArg) => ({
        url: `/dms/v3/document/template/${queryArg.templateId}/download`,
        responseHandler: async (response) => await response.blob(),
      }),
    }),
    createCustomFieldProperty: build.mutation<
      CreateCustomFieldPropertyApiResponse,
      CreateCustomFieldPropertyApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/custom-field-document-template-property/${queryArg.customFieldDefinitionId}`,
        method: 'PUT',
        body: queryArg.createCustomFieldPropertyRequestBody,
      }),
      invalidatesTags: invalidateOnSuccess(['CustomFieldProperty']),
    }),
    removeCustomFieldProperty: build.mutation<
      RemoveCustomFieldPropertyApiResponse,
      RemoveCustomFieldPropertyApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/custom-field-document-template-property/${queryArg.customFieldDefinitionId}`,
        method: 'DELETE',
      }),
      invalidatesTags: invalidateOnSuccess(['CustomFieldProperty']),
    }),
    listCustomFieldProperty: build.query<
      ListCustomFieldPropertyApiResponse,
      ListCustomFieldPropertyApiArg
    >({
      query: () => ({
        url: `/dms/v1/custom-field-document-template-property`,
      }),
      providesTags: ['CustomFieldProperty'],
    }),
    getDefinitionTemplateList: build.query<
      GetDefinitionTemplateListApiResponse,
      GetDefinitionTemplateListApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v3/document/definition/template`,
        params: {
          withAuthorizationProfileId: queryArg.withAuthorizationProfileId,
          withoutAuthorizationProfile: queryArg.withoutAuthorizationProfile,
        },
      }),
      providesTags: ['DocumentTemplates'],
    }),
    getDocumentGroupList: build.query<GetDocumentGroupListApiResponse, GetDocumentGroupListApiArg>({
      query: () => ({
        url: `/dms/v3/document/group`,
      }),
    }),
    showTemplate: build.mutation<ShowTemplateApiResponse, ShowTemplateApiArg>({
      query: (queryArg) => ({
        url: `/dms/v3/document/template/${queryArg.templateId}/show`,
        method: 'POST',
      }),
      invalidatesTags: invalidateOnSuccess(['DocumentTemplates']),
    }),
    hideTemplate: build.mutation<HideTemplateApiResponse, HideTemplateApiArg>({
      query: (queryArg) => ({
        url: `/dms/v3/document/template/${queryArg.templateId}/hide`,
        method: 'POST',
      }),
      invalidatesTags: invalidateOnSuccess(['DocumentTemplates']),
    }),
    removeTemplate: build.mutation<RemoveTemplateApiResponse, RemoveTemplateApiArg>({
      query: (queryArg) => ({
        url: `/dms/v3/document/template/${queryArg.templateId}`,
        method: 'DELETE',
      }),
      invalidatesTags: invalidateOnSuccess(['DocumentTemplates']),
    }),
    setPrimaryTemplateV3: build.mutation<
      SetPrimaryTemplateApiResponseV3,
      SetPrimaryTemplateApiArgV3
    >({
      query: (queryArg) => ({
        url: `/dms/v3/document/template/${queryArg.templateId}/primary`,
        method: 'POST',
      }),
      invalidatesTags: invalidateOnSuccess(['DocumentTemplates']),
    }),
    downloadTemplateV3: build.query<DownloadTemplateApiResponseV3, DownloadTemplateApiArgV3>({
      query: (queryArg) => ({
        url: `/dms/v3/document/template/${queryArg.templateId}/download`,
        responseHandler: async (response) => await response.blob(),
      }),
    }),
    downloadTemplateSourceV3: build.query<
      DownloadTemplateSourceApiResponseV3,
      DownloadTemplateSourceApiArgV3
    >({
      query: (queryArg) => ({
        url: `/dms/v3/document/template/${queryArg.templateId}/download`,
        headers: {Accept: 'text/plain'},
        responseHandler: async (response) => await response.text(),
      }),
      providesTags: (_result, _error, queryArg) => [
        {type: 'DocumentTemplates', id: queryArg.templateId},
      ],
    }),
    setPrimaryAuthProfile: build.mutation<
      SetPrimaryAuthProfileApiResponse,
      SetPrimaryAuthProfileApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v3/document/template/${queryArg.templateId}/authorization-profile-primary/${queryArg.authorizationProfileId}`,
        method: 'POST',
      }),
      invalidatesTags: invalidateOnSuccess(['DocumentTemplates']),
    }),
    addTemplate: build.mutation<AddTemplateV3ApiResponse, AddTemplateV3ApiArg>({
      query: (queryArg) => ({
        url: `/dms/v3/document/template`,
        method: 'POST',
        body: queryArg.addTemplateRequestBody,
      }),
      invalidatesTags: invalidateOnSuccess(['DocumentTemplates']),
    }),
    patchTemplateV3: build.mutation<PatchTemplateApiResponse, PatchTemplateApiArg>({
      query: (queryArg) => ({
        url: `/dms/v3/document/template/${queryArg.templateId}`,
        method: 'PATCH',
        body: queryArg.patchTemplateRequestBody,
      }),
      invalidatesTags: invalidateOnSuccess((_result, queryArg: PatchTemplateApiArg) => [
        'DocumentTemplates',
        {type: 'DocumentTemplates', id: queryArg.templateId},
      ]),
    }),
    getDocumentDefinitionList: build.query<
      GetDocumentDefinitionListApiResponse,
      GetDocumentDefinitionListApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v3/document/definition`,
        params: {group: queryArg.group},
      }),
      providesTags: ['DocumentTemplates'],
    }),
  }),
});

export const {
  useGetDocumentDefinitionListQuery,
  usePatchTemplateV3Mutation,
  useAddTemplateMutation,
  useRemoveTemplateMutation,
  useShowTemplateMutation,
  useHideTemplateMutation,
  useSetPrimaryTemplateV3Mutation,
  useDownloadTemplateV3Query,
  useLazyDownloadTemplateV3Query,
  useDownloadTemplateSourceV3Query,
  useSetPrimaryAuthProfileMutation,
  useGetDocumentGroupListQuery,
  useGetDefinitionTemplateListQuery,
  useDownloadTemplateQuery,
  useLazyDownloadTemplateQuery,
  useCreateCustomFieldPropertyMutation,
  useRemoveCustomFieldPropertyMutation,
  useListCustomFieldPropertyQuery,
} = documentApi;
